import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Download, Users, PieChart, BarChart3, Filter, Link2, Trophy, UserCheck, ChevronDown, Search, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EDUCATION_LEVEL_LABELS } from "@/lib/educationLevels";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useImpactSurveyUsers, useUserImpactDetail, type ImpactSurveyAnswer } from "@/hooks/useImpactSurveyData";
import { formatMetricLabel, formatMetricValue, calcChangeIndicator } from "@/utils/impactFormatters";

const GENDER_LABELS: Record<string, string> = {
  female: "Female",
  male: "Male",
  other: "Other",
  prefer_not_to_say: "Prefer Not to Say",
};

const AGE_RANGE_LABELS: Record<string, string> = {
  "12-17": "12-17",
  "18-30": "18-30",
  "31-45": "31-45",
  "45+": "45+",
};

// Use the centralized education level labels
const EDUCATION_LABELS = EDUCATION_LEVEL_LABELS;

const HAS_DEVICE_LABELS: Record<string, string> = {
  yes: "Yes",
  no: "No",
  maybe: "Maybe",
};

const REFERRED_BY_LABELS: Record<string, string> = {
  food_for_the_poor: "Food For The Poor",
  word_of_mouth: "Word of Mouth",
  social_media: "Social Media",
  website: "Website",
  other: "Other",
};

interface DemographicData {
  gender: Record<string, number>;
  ageRange: Record<string, number>;
  education: Record<string, number>;
  hasDevice: Record<string, number>;
  referredBy: Record<string, number>;
  parish: Record<string, number>;
  role: Record<string, number>;
  total: number;
}

interface ProfileWithPrivate {
  id: string;
  role: string;
  is_published: boolean;
  gender: string | null;
  age_range: string | null;
  education_level: string | null;
  has_device: string | null;
  referred_by: string | null;
  profile_private: {
    parish: string | null;
  } | null;
}

interface ConnectionWithProfiles {
  id: string;
  status: string;
  entrepreneur_profile: { id: string; role: string; full_name: string; avatar_url: string | null } | null;
  mentor_profile: { id: string; role: string; full_name: string; avatar_url: string | null } | null;
}

interface ConnectionAnalytics {
  total: number;
  mentorEntrepreneur: number;
  entrepreneurToEntrepreneur: number;
  mentorToMentor: number;
  uniqueEntrepreneursConnectedToMentors: number;
  topConnectors: { id: string; name: string; avatar: string | null; role: string; count: number }[];
}

export function DemographicsAnalytics() {
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [publishedFilter, setPublishedFilter] = useState<string>("all");

  const { data: profiles, isLoading } = useQuery({
    queryKey: ["demographics_profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select(`
          id, 
          role, 
          is_published, 
          gender, 
          age_range, 
          education_level, 
          has_device, 
          referred_by,
          profile_private(parish)
        `);

      if (error) throw error;
      return data as ProfileWithPrivate[];
    },
  });

  // Fetch connection data for analytics from unified connections table
  const { data: connections, isLoading: connectionsLoading } = useQuery({
    queryKey: ["connection_analytics"],
    queryFn: async () => {
      const { data: connectionsData, error } = await supabase
        .from("connections")
        .select(`id, status, requester_id, recipient_id`)
        .in("status", ["accepted"]);

      if (error) throw error;
      if (!connectionsData || connectionsData.length === 0) return [];

      // Fetch profiles for all participants
      const allUserIds = [...new Set(
        connectionsData.flatMap(c => [c.requester_id, c.recipient_id])
      )];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, role, full_name, avatar_url")
        .in("id", allUserIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Map to expected format
      return connectionsData.map(c => ({
        id: c.id,
        status: c.status,
        entrepreneur_profile: profileMap.get(c.requester_id) || null,
        mentor_profile: profileMap.get(c.recipient_id) || null,
      })) as ConnectionWithProfiles[];
    },
  });

  const connectionAnalytics = useMemo<ConnectionAnalytics>(() => {
    if (!connections) {
      return {
        total: 0,
        mentorEntrepreneur: 0,
        entrepreneurToEntrepreneur: 0,
        mentorToMentor: 0,
        uniqueEntrepreneursConnectedToMentors: 0,
        topConnectors: [],
      };
    }

    let mentorEntrepreneur = 0;
    let entrepreneurToEntrepreneur = 0;
    let mentorToMentor = 0;
    const entrepreneursConnectedToMentors = new Set<string>();
    const connectorCounts: Record<string, { name: string; avatar: string | null; role: string; count: number }> = {};

    connections.forEach((conn) => {
      const role1 = conn.entrepreneur_profile?.role;
      const role2 = conn.mentor_profile?.role;

      // Categorize connection type
      if ((role1 === "mentor" && role2 === "entrepreneur") || (role1 === "entrepreneur" && role2 === "mentor")) {
        mentorEntrepreneur++;
        // Track entrepreneurs connected to mentors
        if (role1 === "entrepreneur" && conn.entrepreneur_profile) {
          entrepreneursConnectedToMentors.add(conn.entrepreneur_profile.id);
        }
        if (role2 === "entrepreneur" && conn.mentor_profile) {
          entrepreneursConnectedToMentors.add(conn.mentor_profile.id);
        }
      } else if (role1 === "entrepreneur" && role2 === "entrepreneur") {
        entrepreneurToEntrepreneur++;
      } else if (role1 === "mentor" && role2 === "mentor") {
        mentorToMentor++;
      }

      // Count connections per user
      if (conn.entrepreneur_profile) {
        const p = conn.entrepreneur_profile;
        if (!connectorCounts[p.id]) {
          connectorCounts[p.id] = { name: p.full_name, avatar: p.avatar_url, role: p.role, count: 0 };
        }
        connectorCounts[p.id].count++;
      }
      if (conn.mentor_profile) {
        const p = conn.mentor_profile;
        if (!connectorCounts[p.id]) {
          connectorCounts[p.id] = { name: p.full_name, avatar: p.avatar_url, role: p.role, count: 0 };
        }
        connectorCounts[p.id].count++;
      }
    });

    // Get top 10 connectors
    const topConnectors = Object.entries(connectorCounts)
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      total: connections.length,
      mentorEntrepreneur,
      entrepreneurToEntrepreneur,
      mentorToMentor,
      uniqueEntrepreneursConnectedToMentors: entrepreneursConnectedToMentors.size,
      topConnectors,
    };
  }, [connections]);

  const demographics = useMemo<DemographicData>(() => {
    if (!profiles) {
      return {
        gender: {},
        ageRange: {},
        education: {},
        hasDevice: {},
        referredBy: {},
        parish: {},
        role: {},
        total: 0,
      };
    }

    // Apply filters
    let filteredProfiles = profiles;
    if (roleFilter !== "all") {
      filteredProfiles = filteredProfiles.filter((p) => p.role === roleFilter);
    }
    if (publishedFilter !== "all") {
      const isPublished = publishedFilter === "published";
      filteredProfiles = filteredProfiles.filter((p) => p.is_published === isPublished);
    }

    const result: DemographicData = {
      gender: {},
      ageRange: {},
      education: {},
      hasDevice: {},
      referredBy: {},
      parish: {},
      role: {},
      total: filteredProfiles.length,
    };

    filteredProfiles.forEach((profile) => {
      // Gender
      if (profile.gender) {
        result.gender[profile.gender] = (result.gender[profile.gender] || 0) + 1;
      } else {
        result.gender["not_specified"] = (result.gender["not_specified"] || 0) + 1;
      }

      // Age Range
      if (profile.age_range) {
        result.ageRange[profile.age_range] = (result.ageRange[profile.age_range] || 0) + 1;
      } else {
        result.ageRange["not_specified"] = (result.ageRange["not_specified"] || 0) + 1;
      }

      // Education
      if (profile.education_level) {
        result.education[profile.education_level] = (result.education[profile.education_level] || 0) + 1;
      } else {
        result.education["not_specified"] = (result.education["not_specified"] || 0) + 1;
      }

      // Has Device
      if (profile.has_device) {
        result.hasDevice[profile.has_device] = (result.hasDevice[profile.has_device] || 0) + 1;
      } else {
        result.hasDevice["not_specified"] = (result.hasDevice["not_specified"] || 0) + 1;
      }

      // Referred By
      if (profile.referred_by) {
        result.referredBy[profile.referred_by] = (result.referredBy[profile.referred_by] || 0) + 1;
      } else {
        result.referredBy["not_specified"] = (result.referredBy["not_specified"] || 0) + 1;
      }

      // Parish (from profile_private)
      const parish = profile.profile_private?.[0]?.parish;
      if (parish) {
        result.parish[parish] = (result.parish[parish] || 0) + 1;
      } else {
        result.parish["not_specified"] = (result.parish["not_specified"] || 0) + 1;
      }

      // Role
      result.role[profile.role] = (result.role[profile.role] || 0) + 1;
    });

    return result;
  }, [profiles, roleFilter, publishedFilter]);

  const exportToCSV = () => {
    if (!profiles || profiles.length === 0) {
      toast.error("No data to export");
      return;
    }

    // Apply same filters for export
    let filteredProfiles = profiles;
    if (roleFilter !== "all") {
      filteredProfiles = filteredProfiles.filter((p) => p.role === roleFilter);
    }
    if (publishedFilter !== "all") {
      const isPublished = publishedFilter === "published";
      filteredProfiles = filteredProfiles.filter((p) => p.is_published === isPublished);
    }

    const rows: string[][] = [];
    rows.push([
      "Role",
      "Published",
      "Gender",
      "Age Range",
      "Education Level",
      "Has Device",
      "Referred By",
      "Parish",
    ]);

    filteredProfiles.forEach((profile) => {
      const parish = profile.profile_private?.[0]?.parish;
      rows.push([
        profile.role,
        profile.is_published ? "Yes" : "No",
        GENDER_LABELS[profile.gender || ""] || profile.gender || "Not Specified",
        AGE_RANGE_LABELS[profile.age_range || ""] || profile.age_range || "Not Specified",
        EDUCATION_LABELS[profile.education_level || ""] || profile.education_level || "Not Specified",
        HAS_DEVICE_LABELS[profile.has_device || ""] || profile.has_device || "Not Specified",
        REFERRED_BY_LABELS[profile.referred_by || ""] || profile.referred_by || "Not Specified",
        parish || "Not Specified",
      ]);
    });

    const csvContent = rows
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `demographics-${format(new Date(), "yyyy-MM-dd")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success("Demographics exported!");
  };

  const renderDistribution = (
    data: Record<string, number>,
    labels: Record<string, string>,
    title: string,
    icon: React.ReactNode
  ) => {
    const total = Object.values(data).reduce((sum, count) => sum + count, 0);
    const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);

    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            {icon}
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {entries.map(([key, count]) => {
            const percentage = total > 0 ? (count / total) * 100 : 0;
            const label = key === "not_specified" ? "Not Specified" : labels[key] || key;
            return (
              <div key={key} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>{label}</span>
                  <span className="text-muted-foreground">
                    {count} ({percentage.toFixed(1)}%)
                  </span>
                </div>
                <Progress value={percentage} className="h-2" />
              </div>
            );
          })}
          {entries.length === 0 && (
            <p className="text-sm text-muted-foreground">No data available</p>
          )}
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent className="space-y-3">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="space-y-1">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-2 w-full" />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <PieChart className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Demographics Analytics</h3>
          <Badge variant="secondary">{demographics.total} users</Badge>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="entrepreneur">Entrepreneurs</SelectItem>
                <SelectItem value="mentor">Mentors</SelectItem>
                <SelectItem value="admin">Admins</SelectItem>
              </SelectContent>
            </Select>
            <Select value={publishedFilter} onValueChange={setPublishedFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Connection Analytics Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Connection Analytics</h3>
          <Badge variant="secondary">{connectionAnalytics.total} connections</Badge>
        </div>

        {/* Connection Summary Cards */}
        <div className="grid gap-4 md:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Connections</CardTitle>
              <Link2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{connectionAnalytics.total}</div>
              <p className="text-xs text-muted-foreground">completed or accepted</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Mentor ↔ Entrepreneur</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{connectionAnalytics.mentorEntrepreneur}</div>
              <p className="text-xs text-muted-foreground">cross-role connections</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Entrepreneur ↔ Entrepreneur</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{connectionAnalytics.entrepreneurToEntrepreneur}</div>
              <p className="text-xs text-muted-foreground">peer connections</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Mentor ↔ Mentor</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{connectionAnalytics.mentorToMentor}</div>
              <p className="text-xs text-muted-foreground">peer connections</p>
            </CardContent>
          </Card>
          <Card className="border-primary/50 bg-primary/5">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Entrepreneurs w/ Mentors</CardTitle>
              <Trophy className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">{connectionAnalytics.uniqueEntrepreneursConnectedToMentors}</div>
              <p className="text-xs text-muted-foreground">unique entrepreneurs</p>
            </CardContent>
          </Card>
        </div>

        {/* Top Connectors Table */}
        {connectionAnalytics.topConnectors.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                Top Connectors
              </CardTitle>
              <CardDescription>Users with the most connections</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Rank</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Connections</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {connectionAnalytics.topConnectors.map((connector, index) => (
                    <TableRow key={connector.id}>
                      <TableCell className="font-medium">
                        {index < 3 ? (
                          <span className={index === 0 ? "text-yellow-500" : index === 1 ? "text-gray-400" : "text-amber-600"}>
                            {index === 0 ? "🥇" : index === 1 ? "🥈" : "🥉"}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">{index + 1}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={connector.avatar || undefined} />
                            <AvatarFallback>{connector.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{connector.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={connector.role === "mentor" ? "default" : "outline"} className={connector.role === "entrepreneur" ? "bg-primary/10 text-primary border-primary/30" : ""}>
                          {connector.role.charAt(0).toUpperCase() + connector.role.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold">{connector.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{demographics.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Entrepreneurs</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{demographics.role["entrepreneur"] || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mentors</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{demographics.role["mentor"] || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Data Completeness</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {demographics.total > 0
                ? Math.round(
                    ((demographics.total - (demographics.gender["not_specified"] || 0)) /
                      demographics.total) *
                      100
                  )
                : 0}
              %
            </div>
            <p className="text-xs text-muted-foreground">have gender specified</p>
          </CardContent>
        </Card>
      </div>

      {/* Distribution Charts */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {renderDistribution(
          demographics.gender,
          GENDER_LABELS,
          "Gender Distribution",
          <Users className="h-4 w-4" />
        )}
        {renderDistribution(
          demographics.ageRange,
          AGE_RANGE_LABELS,
          "Age Distribution",
          <BarChart3 className="h-4 w-4" />
        )}
        {renderDistribution(
          demographics.education,
          EDUCATION_LABELS,
          "Education Level",
          <BarChart3 className="h-4 w-4" />
        )}
        {renderDistribution(
          demographics.hasDevice,
          HAS_DEVICE_LABELS,
          "Device Access",
          <BarChart3 className="h-4 w-4" />
        )}
        {renderDistribution(
          demographics.referredBy,
          REFERRED_BY_LABELS,
          "Referral Source",
          <BarChart3 className="h-4 w-4" />
        )}
        {renderDistribution(
          demographics.parish,
          {},
          "Parish Distribution",
          <BarChart3 className="h-4 w-4" />
        )}
      </div>

      {/* Impact Survey Data Section */}
      <ImpactSurveyDataSection />
    </div>
  );
}

/* ============= Impact Survey Data Section ============= */

const FONT = { fontFamily: "Public Sans, Arial, sans-serif" } as const;
const ORANGE = "#C94F13";
const GREEN = "#8FCA3F";

function ImpactSurveyDataSection() {
  const { users, isLoading } = useImpactSurveyUsers();
  const [search, setSearch] = useState("");
  const [openUserId, setOpenUserId] = useState<string | null>(null);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => u.fullName.toLowerCase().includes(q));
  }, [users, search]);

  return (
    <div className="space-y-4" style={FONT}>
      <div className="flex items-center gap-2">
        <BarChart3 className="h-5 w-5" />
        <h3 className="text-lg font-semibold">Impact Survey Data</h3>
        <Badge variant="secondary">{users.length} users</Badge>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">
        Responses to impact surveys, tracked across waves.
      </p>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : filteredUsers.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            {users.length === 0
              ? "No users have completed an impact survey yet."
              : "No users match your search."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredUsers.map((u) => (
            <ImpactUserRow
              key={u.userId}
              user={u}
              isOpen={openUserId === u.userId}
              onToggle={() =>
                setOpenUserId((cur) => (cur === u.userId ? null : u.userId))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ImpactUserRow({
  user,
  isOpen,
  onToggle,
}: {
  user: ReturnType<typeof useImpactSurveyUsers>["users"][number];
  isOpen: boolean;
  onToggle: () => void;
}) {
  // Lazy: only fetch detail when open
  const { impactData, isLoading } = useUserImpactDetail(isOpen ? user.userId : null);

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center gap-3 p-4 text-left hover:bg-accent/40 transition-colors"
          >
            <UserAvatar
              avatarPath={user.avatarUrl}
              fullName={user.fullName}
              className="h-10 w-10"
              thumbnailSize={64}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium truncate">{user.fullName}</span>
                {user.role && (
                  <Badge variant="secondary" className="text-xs">
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {user.responseCount} response
                {user.responseCount === 1 ? "" : "s"} across {user.surveyCount}{" "}
                survey{user.surveyCount === 1 ? "" : "s"} · Latest:{" "}
                {user.latestWaveLabel}
              </div>
            </div>
            <ChevronDown
              className={`h-5 w-5 text-muted-foreground transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 pt-2 border-t">
            {isLoading ? (
              <div className="space-y-2 py-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : impactData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-3">
                No impact data found for this user.
              </p>
            ) : (
              <div className="space-y-5 pt-3">
                {impactData.map((survey) => (
                  <div key={survey.surveyId} className="space-y-3">
                    <h4 className="font-bold text-sm">{survey.surveyTitle}</h4>
                    <div
                      className="space-y-3 pl-4"
                      style={{ borderLeft: `3px solid ${ORANGE}` }}
                    >
                      {survey.waves.map((wave, idx) => {
                        const previous = survey.waves[idx + 1];
                        return (
                          <WaveBlock
                            key={`${wave.waveLabel}-${idx}`}
                            wave={wave}
                            previousWave={previous || null}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function WaveBlock({
  wave,
  previousWave,
}: {
  wave: { waveLabel: string; completedAt: string; answers: ImpactSurveyAnswer[] };
  previousWave: { answers: ImpactSurveyAnswer[] } | null;
}) {
  const prevByQuestion = useMemo(() => {
    const m = new Map<string, ImpactSurveyAnswer>();
    (previousWave?.answers || []).forEach((a) => m.set(a.questionId, a));
    return m;
  }, [previousWave]);

  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-3">
        <Badge style={{ backgroundColor: ORANGE, color: "white" }}>
          {wave.waveLabel}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {(() => {
            try {
              return format(new Date(wave.completedAt), "MMM d, yyyy");
            } catch {
              return wave.completedAt;
            }
          })()}
        </span>
      </div>
      <div className="space-y-1.5">
        {wave.answers.map((a) => (
          <AnswerRow
            key={a.questionId}
            answer={a}
            previous={prevByQuestion.get(a.questionId) || null}
          />
        ))}
      </div>
    </div>
  );
}

function AnswerRow({
  answer,
  previous,
}: {
  answer: ImpactSurveyAnswer;
  previous: ImpactSurveyAnswer | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const isMetric = !!answer.impactMetricKey;
  const label = isMetric
    ? formatMetricLabel(answer.impactMetricKey!)
    : answer.questionText;

  // Determine raw + display value
  let rawValue: number | string | null = null;
  if (answer.questionType === "multiple_choice") {
    rawValue = answer.answerChoice?.selected ?? null;
  } else if (answer.questionType === "text") {
    rawValue = answer.answerText;
  } else {
    rawValue = answer.answerNumber;
  }
  let displayValue = formatMetricValue(rawValue, answer.questionType);

  // Text truncation
  if (answer.questionType === "text" && typeof rawValue === "string" && rawValue.length > 120) {
    displayValue = expanded ? rawValue : rawValue.slice(0, 120) + "…";
  }

  // Change indicator (numeric only)
  const isNumeric = ["currency", "number", "rating"].includes(answer.questionType);
  let indicator: React.ReactNode = null;
  if (isNumeric && previous && typeof previous.answerNumber === "number" && typeof answer.answerNumber === "number") {
    const { direction, delta } = calcChangeIndicator(answer.answerNumber, previous.answerNumber);
    if (direction === "up") {
      indicator = (
        <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: GREEN }}>
          <TrendingUp className="h-3.5 w-3.5" />
          {formatMetricValue(delta, answer.questionType)}
        </span>
      );
    } else if (direction === "down") {
      indicator = (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
          <TrendingDown className="h-3.5 w-3.5" />
          {formatMetricValue(delta, answer.questionType)}
        </span>
      );
    } else {
      indicator = (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Minus className="h-3.5 w-3.5" />
        </span>
      );
    }
  }

  return (
    <div
      className={`flex items-start gap-3 text-sm ${
        isMetric ? "" : "opacity-80"
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="font-semibold break-words">
          {displayValue}
          {answer.questionType === "text" &&
            typeof rawValue === "string" &&
            rawValue.length > 120 && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="ml-2 text-xs font-normal text-primary hover:underline"
              >
                {expanded ? "Show less" : "Show more"}
              </button>
            )}
        </div>
      </div>
      {indicator && <div className="pt-4">{indicator}</div>}
    </div>
  );
}
